import { type OpenApiSchemaImportStocksSettings } from '#shared/api/schema';
import { OZON_SERVICE_NAME } from '#shared/lib/brand';
import { INTEGRATION_TYPE_OZON_DELIVERY_FBS, INTEGRATION_TYPE_OZON_FBO_PLUS } from '#shared/lib/integrations';
import { showError } from '#shared/lib/toast';
import { getIntegrationSettingsStocks, updateIntegrationSettingsStocks } from '../api';

const UPDATE_INTEGRATION_SETTINGS_ERROR_LABEL = `Произошла ошибка при подключении ${OZON_SERVICE_NAME} Доставки.`;

/**
 * Добавляет подтип Ozon FBO+ к настройкам импорта остатков (подключение Ozon Доставки).
 *
 * @param additionalSubTypes — дополнительные подтипы интеграции из настроек импорта остатков
 * @param onSuccess — callback функция, которая вызывается после успешного подключения
 * @kind helper
 */
export async function connectOzonLogisticBaseIntegration({
    additionalSubTypes = [],
    onSuccess,
}: {
    additionalSubTypes: NonNullable<OpenApiSchemaImportStocksSettings['sub_types']>;
    onSuccess: VoidFunction;
}) {
    try {
        const integrationSettings = await getIntegrationSettingsStocks();
        const currentSubTypes = integrationSettings.sub_types ?? [];

        const existingTypesMap = new Map(currentSubTypes.map((subType) => [subType.sub_type, subType]));

        /** Добавляем только те новые типы, которых еще нет в текущих настройках */
        const newSubTypes = additionalSubTypes.filter((subType) => !existingTypesMap.has(subType.sub_type));

        const payload: OpenApiSchemaImportStocksSettings = {
            sub_types: [...currentSubTypes, ...newSubTypes],
        };

        await updateIntegrationSettingsStocks(payload);
        await onSuccess();
    } catch (error) {
        showError({ label: UPDATE_INTEGRATION_SETTINGS_ERROR_LABEL }, error);
    }
}

export async function connectOzonLogisticFbsIntegration({ onSuccess }: { onSuccess: VoidFunction }): Promise<void> {
    return connectOzonLogisticBaseIntegration({
        additionalSubTypes: [{ sub_type: INTEGRATION_TYPE_OZON_DELIVERY_FBS }],
        onSuccess,
    });
}

export async function connectOzonLogisticFboIntegration({ onSuccess }: { onSuccess: VoidFunction }): Promise<void> {
    return connectOzonLogisticBaseIntegration({
        additionalSubTypes: [{ sub_type: INTEGRATION_TYPE_OZON_FBO_PLUS }],
        onSuccess,
    });
}

export async function connectOzonLogisticFboAndFbsIntegration({
    onSuccess,
}: {
    onSuccess: VoidFunction;
}): Promise<void> {
    return connectOzonLogisticBaseIntegration({
        additionalSubTypes: [
            { sub_type: INTEGRATION_TYPE_OZON_FBO_PLUS },
            { sub_type: INTEGRATION_TYPE_OZON_DELIVERY_FBS },
        ],
        onSuccess,
    });
}
